import React from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import { Bar } from 'react-chartjs-2';
import type { Chart as ChartJS } from 'chart.js';
import { BACKLOG_LABELS, BACKLOG_KEYS, BACKLOG_COLORS } from '../types';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK, formatDataLabelCompact } from '../chartFormatters';

export interface KpiBacklogBarChartProps {
  title: string;
  data: Record<string, number>;
  onBucketClick?: (bucketKey: string, label: string) => void;
}

const KpiBacklogBarChart: React.FC<KpiBacklogBarChartProps> = ({ title, data, onBucketClick }) => {
  const chartRef = React.useRef<ChartJS<'bar'>>(null);

  const onClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onBucketClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const first = els[0];
    if (first == null) return;
    const idx = first.index;
    const key = BACKLOG_KEYS[idx];
    const label = BACKLOG_LABELS[idx];
    if (key) onBucketClick(key, label);
  };

  return (
  <KpiChartPanel
    title={title}
    className="flex h-full min-h-0 flex-col"
    bodyClassName="flex min-h-0 flex-1 flex-col px-3 pb-4 pt-4"
  >
    <div className="relative min-h-0 w-full flex-1">
      <Bar
        ref={chartRef}
        onClick={onBucketClick ? onClick : undefined}
        data={{
          labels: BACKLOG_LABELS,
          datasets: [
            {
              label: 'Tickets',
              data: BACKLOG_KEYS.map((k) => data[k] ?? 0),
              backgroundColor: BACKLOG_COLORS,
              borderRadius: 8,
              barThickness: 28,
              datalabels: {
                display: true,
                anchor: 'end',
                align: 'end',
                offset: 4,
                color: '#334155',
                font: { weight: 600, size: 11 },
                formatter: (value: number) => formatDataLabelCompact(Number(value)),
              },
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 20 } },
          onHover: (event, elements) => {
            if (event.native?.target instanceof HTMLElement) {
              event.native.target.style.cursor =
                onBucketClick && elements.length > 0 ? 'pointer' : 'default';
            }
          },
          plugins: {
            legend: { display: false },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: '#64748b', font: { size: 11 } },
            },
            y: {
              display: true,
              beginAtZero: true,
              ticks: {
                precision: 0,
                color: '#94a3b8',
                callback: (v) => formatAxisCompactK(v as number),
              },
              grid: { color: 'rgba(148, 163, 184, 0.15)' },
              border: { display: false },
            },
          },
        }}
      />
    </div>
  </KpiChartPanel>
  );
};

export default KpiBacklogBarChart;
